import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Download, Edit3, FolderInput, Hash, MoreHorizontal, Share2, Trash2, Copy, Check, ChevronDown, ExternalLink } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import { getNoteDoc } from "./note-store";
import DOMPurify from "dompurify";

export default function NoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const note = useMemo(() => (id ? getNoteDoc(id) : null), [id]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const pageShell = "flex h-screen overflow-hidden bg-white text-foreground";
  const card = "bg-white";
  const actionBtn = "inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB]";

  if (!note) {
    return (
      <div className="flex h-screen overflow-hidden bg-[#F6F7FB] text-foreground">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex min-w-0 flex-1 items-center justify-center bg-background">
          <div className="text-center">
            <p className="mb-3 text-sm text-muted-foreground">笔记不存在</p>
            <button onClick={() => navigate("/knowledge")} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium shadow-sm hover:bg-accent">
              <ArrowLeft className="h-4 w-4" /> 返回知识库
            </button>
          </div>
        </main>
      </div>
    );
  }

  const typeColorClass = "hidden";
  const menuCard = "absolute right-0 top-full mt-2 w-52 rounded-2xl border border-[#E5E7EB] bg-white p-2 shadow-[0_12px_30px_rgba(15,23,42,0.08)] z-50";

  return (
    <div className={pageShell}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
        <div className="bg-white">
          <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center gap-3 px-0">
            <button onClick={() => navigate(-1)} className={actionBtn}>
              <ArrowLeft className="h-4 w-4" /> 笔记
            </button>
            <span className="text-[#6B7280]">/</span>
            <h1 className="truncate text-base font-semibold tracking-tight text-[#111827]">{note.title}</h1>
            <span className={typeColorClass}>type</span>
            <div className="ml-auto flex items-center gap-2">
              <button className={actionBtn} onClick={() => navigate(`/knowledge/note/${id}/edit`)}>
                <Edit3 className="h-4 w-4" />编辑
              </button>
              <div className="relative">
                <button className={actionBtn} onClick={() => { setShowShareMenu((v) => !v); setShowMoreMenu(false); }}>
                  <Share2 className="h-4 w-4" />分享
                </button>
                {showShareMenu && (
                  <div className={menuCard}>
                    <button onClick={handleCopyLink} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F9FAFB]">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? "已复制链接" : "复制链接"}
                    </button>
                    <button className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F9FAFB]">
                      <ExternalLink className="h-4 w-4" /> 打开公开分享页
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <button className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#111827] transition-colors hover:bg-[#F9FAFB]" onClick={() => { setShowMoreMenu((v) => !v); setShowShareMenu(false); }}>
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {showMoreMenu && (
                  <div className={menuCard}>
                    <button className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F9FAFB]">
                      <FolderInput className="h-4 w-4" /> 入库
                    </button>
                    <button className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F9FAFB]">
                      <Download className="h-4 w-4" /> 导出
                    </button>
                    <button className="flex w-full items-center gap-2 rounded-xl bg-white px-3 py-2 text-left text-sm font-medium text-destructive transition-colors hover:bg-white hover:text-destructive">
                      <Trash2 className="h-4 w-4 shrink-0 text-destructive" /> 删除
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-0 py-6">
          <div className="mx-auto w-full max-w-[1100px] px-0">
            <section className="max-w-3xl">
              <h2 className="text-3xl font-semibold tracking-tight text-[#111827]">{note.title}</h2>
              {note.subtitle ? <p className="mt-4 text-sm leading-8 text-[#4B5563]">{note.subtitle}</p> : null}
              <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {note.updatedAt}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-1.5">
                  <Hash className="h-4 w-4" />
                  {(note.content || "").replace(/\s/g, "").length.toLocaleString()} 字
                </span>
                {note.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-1.5 text-[#4B5563]">
                    {tag}
                  </span>
                ))}
              </div>
            </section>

            <article className={`${card} mt-8 rounded-2xl border border-[#E5E7EB] p-6`}>
              {note.format === "html" ? (
                <div
                  className="prose prose-zinc max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(note.content || "", {
                      USE_PROFILES: { html: true },
                      ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "a", "img", "h2", "h3", "ul", "ol", "li", "blockquote", "code", "pre", "span", "div"],
                      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "style"],
                    }),
                  }}
                />
              ) : (
                <MarkdownLite content={note.content} />
              )}
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}

function MarkdownLite({ content }: { content: string }) {
  const blocks = useMemo(() => {
    const lines = (content || "").replace(/\r\n/g, "\n").split("\n");
    const out: Array<{ kind: "h2" | "h3" | "p" | "img"; text?: string; alt?: string; src?: string }> = [];
    let buf: string[] = [];

    const flush = () => {
      const txt = buf.join("\n").trim();
      if (txt) out.push({ kind: "p", text: txt });
      buf = [];
    };

    for (const raw of lines) {
      const line = raw.trimEnd();
      const t = line.trim();
      if (!t) {
        flush();
        continue;
      }

      const img = t.match(/^!\[(.*)\]\((.*)\)$/);
      if (img) {
        flush();
        out.push({ kind: "img", alt: img[1] || "image", src: img[2] });
        continue;
      }

      const h2 = t.match(/^##\s+(.*)$/);
      const h3 = t.match(/^###\s+(.*)$/);
      if (h2) {
        flush();
        out.push({ kind: "h2", text: h2[1] });
        continue;
      }
      if (h3) {
        flush();
        out.push({ kind: "h3", text: h3[1] });
        continue;
      }

      buf.push(raw);
    }
    flush();
    return out;
  }, [content]);

  if (!blocks.length) {
    return <div className="text-sm text-muted-foreground">暂无正文内容</div>;
  }

  return (
    <div className="space-y-5">
      {blocks.map((b, i) => {
        if (b.kind === "h2") return <h3 key={i} className="text-xl font-semibold tracking-tight text-[#111827]">{b.text}</h3>;
        if (b.kind === "h3") return <h4 key={i} className="text-lg font-semibold tracking-tight text-[#111827]">{b.text}</h4>;
        if (b.kind === "img")
          return (
            <figure key={i} className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
              <img src={b.src} alt={b.alt} className="max-h-[520px] w-full object-contain" />
              {b.alt ? <figcaption className="border-t border-[#E5E7EB] px-4 py-2 text-xs text-[#6B7280]">{b.alt}</figcaption> : null}
            </figure>
          );
        return (
          <p key={i} className="whitespace-pre-wrap text-sm leading-8 text-[#4B5563]">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}
